import React, { useEffect, useMemo, useState } from 'react';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import useModalEscape from '../../hooks/useModalEscape';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { notifyError, notifySuccess } from '../../notify';
import Sidebar from '../../components/Sidebar/Sidebar';
import BackendServiceControls from './SA_ControlCenter';
import './SA_Security.css';

// lucide-static icons
import { 
  Search, 
  CircleAlert,
  MoreVertical,
  X,
  Trash2,
  EyeOff,
  Eye,
  AlertTriangle
} from 'lucide-static';

// Icon wrapper component to safely render SVG strings
const Icon = ({ icon, className, size = 20, color, ...props }) => {
  const getProcessedSvg = () => {
    let svgString = icon;
    
    if (size) {
      svgString = svgString.replace('<svg', `<svg width="${size}" height="${size}"`);
    }
    
    if (color) {
      svgString = svgString.replace('<svg', `<svg color="${color}" stroke="${color}"`);
    }
    
    if (className) {
      svgString = svgString.replace('<svg', `<svg class="${className}"`);
    }
    
    return svgString;
  };

  return <span dangerouslySetInnerHTML={{ __html: getProcessedSvg() }} {...props} />;
};

const SA_Security = () => {
  useDocumentTitle("Avisa Admin Dashboard");

  const { getIdToken } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const { securityAlerts: contextAlerts, securityAlertsLoaded, refreshSecurityAlerts, updateSecurityAlertStatus, deleteSecurityAlert } = useData();
  const [securityAlerts, setSecurityAlerts] = useState(contextAlerts);
  const [loading, setLoading] = useState(!securityAlertsLoaded);
  const ALERTS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [testEmail, setTestEmail] = useState('');
  
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [isActionSubmitting, setIsActionSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState(null);
  const [isDeleteSubmitting, setIsDeleteSubmitting] = useState(false);

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setAlertToDelete(null);
    setIsDeleteSubmitting(false);
  };

  useModalEscape(() => {
    if (isDeleteModalOpen) {
      closeDeleteModal();
    } else {
      setActiveMenuId(null);
    }
  }, !!activeMenuId || isDeleteModalOpen);

  useEffect(() => {
    setSecurityAlerts(contextAlerts);
    if (securityAlertsLoaded) {
      setLoading(false);
      setTotalPages(Math.ceil(contextAlerts.length / ALERTS_PER_PAGE) || 1);
    }
  }, [contextAlerts, securityAlertsLoaded]);

  const fetchSecurityAlerts = refreshSecurityAlerts;

  // Test function to create failed login attempts with user-provided email
  const testFailedLogins = async () => {
    if (!testEmail || !testEmail.trim()) {
      notifyError('Please enter an email address to test with');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmail)) {
      notifyError('Please enter a valid email address');
      return;
    }

    try {
      const token = await getIdToken();
      
      for(let i = 1; i <= 11; i++) {
        console.log(`Sending attempt ${i} for ${testEmail}...`);
        
        const response = await fetch('https://avisa-huja.onrender.com/api/security/track-failed-login', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            email: testEmail.trim(), // Use the actual email from input
            ipAddress: `192.168.1.${100 + i}`,
            userAgent: navigator.userAgent,
            isAdminLogin: true
          })
        });
        
        const data = await response.json();
        console.log(`Attempt ${i} response:`, data);
        
        // Wait a bit between attempts
        await new Promise(r => setTimeout(r, 500));
      }
      
      notifySuccess(`Created 11 failed login attempts for ${testEmail}! Refresh to see alerts.`);
      fetchSecurityAlerts(); // Refresh the alerts
      setTestEmail(''); // Clear the email input after successful test
    } catch (error) {
      console.error('Test failed:', error);
      notifyError('Error creating test alerts: ' + error.message);
    }
  };

  useEffect(() => {
    refreshSecurityAlerts();
  }, [refreshSecurityAlerts]);

  const handleDismiss = async (alertId) => {
    if (isActionSubmitting) return;
    setIsActionSubmitting(true);
    try {
      const result = await updateSecurityAlertStatus(alertId, 'dismissed');
      if (result.ok) {
        notifySuccess('Alert dismissed.');
        setActiveMenuId(null);
      } else {
        notifyError(result?.error || 'Failed to dismiss alert.');
      }
    } catch (error) {
      console.error('Dismiss error:', error);
      notifyError(error?.message || 'Failed to dismiss alert.');
    } finally {
      setIsActionSubmitting(false);
    }
  };

  const handleUndismiss = async (alertId) => {
    if (isActionSubmitting) return;
    setIsActionSubmitting(true);
    try {
      const result = await updateSecurityAlertStatus(alertId, 'new');
      if (result.ok) {
        notifySuccess('Alert marked as active.');
        setActiveMenuId(null);
      } else {
        notifyError(result?.error || 'Failed to undismiss alert.');
      }
    } catch (error) {
      console.error('Undismiss error:', error);
      notifyError(error?.message || 'Failed to undismiss alert.');
    } finally {
      setIsActionSubmitting(false);
    }
  };

  const openDeleteModal = (alert) => {
    setAlertToDelete(alert);
    setIsDeleteModalOpen(true);
    setActiveMenuId(null);
  };

  const confirmDeleteAlert = async () => {
    if (isDeleteSubmitting || !alertToDelete?.id) return;

    setIsDeleteSubmitting(true);
    try {
      const result = await deleteSecurityAlert(alertToDelete.id);
      if (result.ok) {
        notifySuccess('Security alert deleted.');
        closeDeleteModal();
      } else {
        notifyError(result?.error || 'Failed to delete alert.');
      }
    } catch (error) {
      console.error('Delete error:', error);
      notifyError(error?.message || 'Failed to delete alert.');
    } finally {
      setIsDeleteSubmitting(false);
    }
  };

  const toggleMenu = (e, alertId) => {
    e.stopPropagation();
    setActiveMenuId(activeMenuId === alertId ? null : alertId);
  };

  useEffect(() => {
    const handleClickOutside = () => setActiveMenuId(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  const filteredAlerts = useMemo(() => {
    if (!searchTerm.trim()) return securityAlerts;
    const term = searchTerm.toLowerCase();
    return securityAlerts.filter((alert) =>
      (alert.title && alert.title.toLowerCase().includes(term)) ||
      (alert.details && alert.details.toLowerCase().includes(term)) ||
      (alert.time && alert.time.toLowerCase().includes(term))
    );
  }, [securityAlerts, searchTerm]);

  const paginatedAlerts = useMemo(() => {
    const sorted = [...filteredAlerts].sort((a, b) => new Date(b.time) - new Date(a.time));
    const start = (currentPage - 1) * ALERTS_PER_PAGE;
    return sorted.slice(start, start + ALERTS_PER_PAGE);
  }, [filteredAlerts, currentPage]);

  useEffect(() => {
    setTotalPages(Math.ceil(filteredAlerts.length / ALERTS_PER_PAGE) || 1);
  }, [filteredAlerts]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, securityAlerts]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <main className="main-content">
        {/* Top Bar with Search */}
        <div className="security-top-bar">
          <div className="security-search-section">
            <div className="security-search-wrapper">
              <Icon icon={Search} size={18} className="security-search-icon" />
              <input
                type="text"
                placeholder="Search Security Alerts..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  if (e.target.value === '') {
                    setSearchTerm('');
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearchTerm(searchInput);
                  }
                }}
                className="security-search-input"
              />
            </div>
          </div>
          
        </div>

        {/* Security Alerts List Section */}
        <div className="security-alerts-card">
          <h1 className="card-title">Security Alerts</h1>
          <p className="card-subtitle">Monitor and manage system security notifications and events</p>
          
          <div className="alerts-list">
            {loading ? (
              <div className="no-alerts-state">
                <Icon icon={CircleAlert} size={48} className="no-alerts-icon" />
                <span>Loading security alerts...</span>
              </div>
            ) : paginatedAlerts.length > 0 ? (
              paginatedAlerts.map((alert, index) => (
                <div key={alert.id || index} className={`alert-card ${alert.status === 'dismissed' ? 'dismissed' : ''}`}>
                  <div className="alert-left">
                    <Icon icon={CircleAlert} size={28} className="alert-icon" />
                    <div className="alert-text">
                      <div className="alert-title-row">
                        <div className="alert-title">{alert.title}</div>
                        {alert.status === 'dismissed' && (
                          <div className="alert-status-badge dismissed">Dismissed</div>
                        )}
                      </div>
                      <div className="alert-details">{alert.details}</div>
                    </div>
                  </div>
                  <div className="alert-right">
                    <div className="alert-time">{alert.time}</div>
                    <div className="alert-actions-wrapper">
                      <button 
                        className={`alert-action-trigger ${activeMenuId === alert.id ? 'active' : ''}`}
                        onClick={(e) => toggleMenu(e, alert.id)}
                      >
                        <Icon icon={MoreVertical} size={20} />
                      </button>

                      {activeMenuId === alert.id && (
                        <div className="alert-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                          <div className="alert-dropdown-header">
                            <span>Alert Actions</span>
                            <button onClick={() => setActiveMenuId(null)}><Icon icon={X} size={14} /></button>
                          </div>
                          <div className="alert-dropdown-items">
                            {alert.status === 'dismissed' ? (
                              <button 
                                className="alert-dropdown-item" 
                                onClick={() => handleUndismiss(alert.id)}
                                disabled={isActionSubmitting}
                              >
                                <Icon icon={Eye} size={16} />
                                <span>Undismiss</span>
                              </button>
                            ) : (
                              <button 
                                className="alert-dropdown-item" 
                                onClick={() => handleDismiss(alert.id)}
                                disabled={isActionSubmitting}
                              >
                                <Icon icon={EyeOff} size={16} />
                                <span>Dismiss</span>
                              </button>
                            )}
                            <button 
                              className="alert-dropdown-item delete" 
                              onClick={() => openDeleteModal(alert)}
                              disabled={isActionSubmitting || isDeleteSubmitting}
                            >
                              <Icon icon={Trash2} size={16} />
                              <span>Delete</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-alerts" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                No security alerts found matching your search.
              </div>
            )}
          </div>

          {securityAlerts.length > 0 && (
            <div className="security-pagination">
              <span className="security-pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <div className="security-pagination-actions">
                <button
                  type="button"
                  className="security-pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                {(() => {
                  const maxVisiblePages = 20;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = startPage + maxVisiblePages - 1;

                  if (endPage > totalPages) {
                    endPage = totalPages;
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(i);
                  }

                  return pages.map((page) => (
                    <button
                      key={page}
                      type="button"
                      className={`security-pagination-btn ${currentPage === page ? "active" : ""}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ));
                })()}
                <button
                  type="button"
                  className="security-pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="security-backend-controls"><BackendServiceControls /></div>

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && (
          <div className="delete-modal-overlay" onClick={closeDeleteModal}>
            <div
              className="delete-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-modal-header">
                <div className="delete-warning-icon">
                  <Icon icon={AlertTriangle} size={32} />
                </div>
                <h2 className="delete-modal-title">Delete Security Alert?</h2>
                <p className="delete-modal-subtitle">
                  Are you sure you want to delete{" "}
                  <strong>{alertToDelete?.title || "this alert"}</strong>? This action cannot be
                  undone.
                </p>
              </div>
              <div className="delete-modal-footer">
                <button
                  type="button"
                  className="delete-cancel-btn"
                  onClick={closeDeleteModal}
                  disabled={isDeleteSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="delete-confirm-btn"
                  onClick={confirmDeleteAlert}
                  disabled={isDeleteSubmitting}
                >
                  {isDeleteSubmitting ? "Deleting..." : "Confirm Deletion"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SA_Security;
